import update from 'immutability-helper';
import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import CenteredSpinner from '../../../../../Utility/CenteredSpinner';
import { PATH_KEY_TO_POSITION } from '../../constants';
import {
  DispatchContext,
  StateContext,
} from '../../contexts/use-menu-editor-context';
import { capitalizeFirstLetter, checkHttpError } from '../../utils';
import AddButton from '../shared/AddButton';
import { PrepAreaTooltip, StatusTooltip } from '../shared/TooltipModals';
import MenuEditor from './menu-editor';
import MenuItem from './menu-item';

const LEVEL = PATH_KEY_TO_POSITION['menuIdx'];

const SectionContainer = styled.div``;

const AddButtonContainer = styled.div`
  padding: 20px 30px 20px 30px;
`;

const ListPadding = styled.div`
  padding: 30px 20px 20px;

  @media only screen and (max-width: 680px) {
    padding: 15px 10px;
  }
`;

const ListContainer = styled.div`
  background-color: white;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  max-width: 1120px;
  margin: auto;

  h1 {
    font-size: 18px;
  }
`;

const ListHeader = styled.div`
  padding: 30px 30px 0 30px;
  border-bottom: 1px #f3f3f3 solid;
  overflow: hidden;
  h1 {
    font-size: 32px;
    font-weight: 500;
  }
`;

const ListHeaderLeft = styled.div`
  width: 75%;
  float: left;
  letter-spacing: -0.6px;
`;

const ListHeaderRight = styled.div`
  width: 25%;
  float: right;
  line-height: 42px;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
`;

const PrepAreaSpan = styled.span`
  margin-right: 36px;

  @media (max-width: 900px) {
    display: none;
  }

  @media (max-width: 1220px) {
    margin-right: calc((26 / 320) * 100vw - 63px);
  }
`;

const StatusSpan = styled.span`
  margin-right: 47px;

  @media (max-width: 900px) {
    display: none;
  }

  @media (max-width: 1220px) {
    margin-right: calc((32 / 320) * 100vw - 75px);
  }
`;

export default function MenuList(props) {
  const context = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const [isUpdatingMenuOrder, setIsUpdatingMenuOrder] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState({});

  const { acceptPreorders } = props;

  const {
    state: { menus, loading, error },
    client,
  } = context;

  // @todo - this should live with the other hooks ideally
  // it's slightly different because it builds the entire list
  // but it's slightly jarring that it's here
  useEffect(() => {
    dispatch({ type: 'FETCH_MENUS_REQUEST' });
    (async () => {
      const payload = await client.post(
        '/fetch-menus?include_draft&include_clients'
      );
      if (checkHttpError(payload)) {
        const message = capitalizeFirstLetter(payload.data.description);
        dispatch({ type: 'FETCH_MENUS_ERROR', payload: message });
        return;
      }
      dispatch({ type: 'FETCH_MENUS_RESPONSE', payload: payload.data || [] });
    })();
  }, []);

  const [showMenuEditor, setShowMenuEditor] = useState(false);
  const toggleEditor = () => setShowMenuEditor(!showMenuEditor);
  const setMenus = async newMenus => {
    const menuPositions = newMenus.map(newMenu => {
      const menuObj = {
        state: 'reposition',
        menu: {
          id: newMenu.id,
          position: newMenus.indexOf(newMenu),
        },
      };
      return menuObj;
    });
    const oldMenus = [...menus];
    dispatch({ type: 'FETCH_MENUS_RESPONSE', payload: newMenus });
    setIsUpdatingMenuOrder(true);
    try {
      await client.post('/menu-operations', menuPositions);
    } catch (err) {
      console.log(err);
      dispatch({ type: 'FETCH_MENUS_RESPONSE', payload: oldMenus });
    } finally {
      setIsUpdatingMenuOrder(false);
    }
  };

  const reorderCards = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.source && !!result.destination) {
      if (result.destination.index === result.source.index) {
        return;
      }
      const dragCard = menus[result.source.index];
      const newMenus = update(menus, {
        $splice: [
          [result.source.index, 1],
          [result.destination.index, 0, dragCard],
        ],
      });
      setMenus(newMenus);
    }
  };

  const grid = 0;
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: 0 * grid,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: 'white',

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const isAnyChildItemExpanded = menus.some(menu => {
    return Boolean(isExpanded[menu.id]);
  });

  return (
    <ListPadding>
      <ListContainer>
        <ListHeader>
          <ListHeaderLeft>
            <h1>Menus</h1>
          </ListHeaderLeft>
          <ListHeaderRight>
            <PrepAreaSpan>
              Prep Area
              <PrepAreaTooltip />
            </PrepAreaSpan>
            <StatusSpan>
              Status
              <StatusTooltip />
            </StatusSpan>
          </ListHeaderRight>
        </ListHeader>

        {loading ? (
          <CenteredSpinner />
        ) : (
          <SectionContainer>
            <DragDropContext onDragEnd={reorderCards}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {(menus || []).map((menu, idx) => {
                      return (
                        <Draggable
                          key={menu.id}
                          draggableId={menu.id}
                          index={idx}
                          isDragDisabled={
                            isUpdatingMenuOrder ||
                            showMenuEditor ||
                            isEditMode ||
                            isAnyChildItemExpanded
                          }
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                            >
                              <MenuItem
                                acceptPreorders={acceptPreorders}
                                key={`menu-item-${menu.id}`}
                                menuIdx={idx}
                                menu={menu}
                                setIsEditMode={setIsEditMode}
                                isEditMode={isEditMode}
                                setIsExpanded={setIsExpanded}
                                isExpanded={isExpanded}
                                showMenuEditorLevel0={showMenuEditor}
                                isUpdatingMenuOrder={isUpdatingMenuOrder}
                                isAnyChildItemExpanded={isAnyChildItemExpanded}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {showMenuEditor ? (
              <MenuEditor
                acceptPreorders={acceptPreorders}
                onCancel={toggleEditor}
                setShowEditor={setShowMenuEditor}
              />
            ) : (
              <AddButtonContainer level={LEVEL}>
                <AddButton onClick={toggleEditor}>Add a Menu</AddButton>
              </AddButtonContainer>
            )}
          </SectionContainer>
        )}

        {error ? <p className="alert alert-danger">{error}</p> : null}
      </ListContainer>
    </ListPadding>
  );
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/MenuList/menu-list.jsx